import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Radio, RadioGroup, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { OpenInNewOutlined } from '@mui/icons-material'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CategoryService from '../../../../shared/services/category/category.service';
import { ICategory, } from '../../../../shared/services/category/category.model';
import { IAllowedInputDates } from '../../../../context/report-periods/report-periods.model';
import GroupService from '../../../../shared/services/group/group.service';
import { IGroupWithCategory } from '../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category';
import { IEditFileUploadLine, IFileUploadLine } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import RuleService from '../../../../shared/services/rule/rule.service';
import { IRule } from '../../../../shared/services/rule/rule.model';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { EmissionsFilterLogic } from '../../../presentation-components/emissons-components/overview/emissions-filter/emissions-filter-logic';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { EmissionsInputAdvanced } from '../../../presentation-components/emissons-components/overview/emissions-input/emissions-input-advanced-form';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import LocationService from '../../../../shared/services/location/location.service';
import { ILocation } from '../../../../shared/services/location/location.model';
import { isEtsGroupId } from '../../../../shared/utils/group-ids-utils';
import { findCategoryByText, getCategoryDisplayName } from '../../../../shared/utils/category-utils';
import { ELECTRICITY_CATEGORY_ID } from '../../../../shared/constants/category-ids';
import { openBillDocument } from '../../../../shared/utils/open-bill-document';

export const EditUploadLineEmissionDialog: FunctionComponent<{
  editUploadLine: IFileUploadLine,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLine) => void,
  isSubmitting: boolean,
}> = ({
  editUploadLine,
  open,
  closeHandler,
  submitHandler,
  isSubmitting }) => {
    //Services
    const groupService = new GroupService();
    const ruleService = new RuleService();
    const categoryService = new CategoryService();
    const subsidiaryService = SubsidiaryService.getInstance();
    const conversionSourceService = new ConversionSourceService();
    const customFieldService = new CustomFieldService()
    const locationService = new LocationService();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    const reportPeriodContext = useContext(ReportPeriodContext)
    const userNotificationsContext = useContext(UserNotificationsContext)

    const viewBillHandler = async () => {
      if (!editUploadLine.billDocumentId) return
      const opened = await openBillDocument(editUploadLine.billDocumentId)
      if (!opened) {
        userNotificationsContext.openSnackbar({ open: true, message: 'Could not open this bill. Please try again.', backgroundColor: 'text.primary' })
      }
    }
    //State
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
    const [conversionSourceId, setConversionSourceId] = useState<string>()
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [groups, setGroups] = useState<IGroupWithCategory[]>()
    const [rules, setRules] = useState<IRule[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [locations, setLocations] = useState<ILocation[]>()
    const [calendarYear, setCalendarYear] = useState<string>();
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [group, setGroup] = useState<IGroupWithCategory>()
    const [allowedCategories, setAllowedCategories] = useState<ICategory[]>()
    const [categories, setCategories] = useState<ICategory[]>()
    const [categoryId, setCategoryId] = useState<string>()
    const [displayFilters, setDisplayFilters] = useState<boolean>(true)
    const [originalRuleId, setOriginalRuleId] = useState<string | null>(null)
    const [originalConversionId, setOriginalConversionId] = useState<string | null>(null)
    const [originalFilterValues, setOriginalFilterValues] = useState<{
      groupId?: string;
      categoryName?: string;
      detailLevelOne?: string;
      detailLevelTwo?: string;
      detailLevelThree?: string;
      detailLevelFour?: string;
      detailLevelFive?: string;
      measurementUnitName?: string;
      conversionSourceId?: string;
    } | null>(null)
    const [allowedDates, setAllowedDates] = useState<IAllowedInputDates>({ startDate: undefined, endDate: undefined })

    const editUploadSchema = Yup.object().shape({
      recordMethod: Yup.string().required('Required'),
      ruleId: Yup.string().when('recordMethod', {
        is: 'Custom Rule',
        then: schema => schema.required('Rule is required'),
        otherwise: schema => schema.notRequired()
      }),
      groupId: Yup.string().when('recordMethod', {
        is: 'Filter Data',
        then: schema => schema.required('Group is required'),
        otherwise: schema => schema.notRequired()
      }),
      categoryName: Yup.string().when('recordMethod', {
        is: 'Filter Data',
        then: schema => schema.required('Category is required'),
        otherwise: schema => schema.notRequired()
      }),
      detailLevelOne: Yup.string().when('recordMethod', {
        is: 'Filter Data',
        then: schema => schema.required('Factor Detail 1 is required'),
        otherwise: schema => schema.notRequired()
      }),
      detailLevelTwo: Yup.string().when('recordMethod', {
        is: 'Filter Data',
        then: schema => schema.required('Factor Detail 2 is required'),
        otherwise: schema => schema.notRequired()
      }),
      detailLevelThree: Yup.string().nullable(),
      detailLevelFour: Yup.string().nullable(),
      detailLevelFive: Yup.string().nullable(),
      measurementUnitName: Yup.string().when('recordMethod', {
        is: 'Filter Data',
        then: schema => schema.required('Unit of Measurement is required'),
        otherwise: schema => schema.notRequired()
      }),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      startDate: Yup.string().required('Start date is required'),
      endDate: Yup.string()
        .required('End date is required')
        .test('not-before-start', 'End date cannot be before start date', function (value) {
          if (!value || !this.parent.startDate) return true;
          return moment(value).isSameOrAfter(moment(this.parent.startDate), 'day');
        }),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      countryId: Yup.string().required('Country is required'),
      projectId: Yup.string().nullable(),
      locationId: Yup.string().nullable(),
      contactSupplierId: Yup.string().nullable(),
      vehicleId: Yup.string().nullable(),
      partOfEts: Yup.boolean(),
      regulatedEmissionTradingSchemeName: Yup.string().when('partOfEts', {
        is: true,
        then: schema => schema.required('You need to select a regulated emission trading scheme'),
        otherwise: schema => schema.nullable().notRequired()
      }),
      customMarketBasedMultiplier: Yup.number().nullable(),
    })

    const formik = useFormik<any>({
      initialValues: {
        recordMethod: editUploadLine.ruleId ? 'Custom Rule' : (editUploadLine as any).conversionId ? 'Filter Data' : editUploadLine.groupId ? 'Filter Data' : 'Custom Rule',
        groupId: editUploadLine.groupId,
        categoryName: editUploadLine.categoryName,
        categoryId: '',
        detailLevelOne: editUploadLine.detailLevelOne,
        detailLevelTwo: editUploadLine.detailLevelTwo,
        detailLevelThree: editUploadLine.detailLevelThree,
        detailLevelFour: editUploadLine.detailLevelFour,
        detailLevelFive: editUploadLine.detailLevelFive,
        measurementUnitName: editUploadLine.measurementUnitName,
        returnId: (editUploadLine as any).conversionId || '',
        unitsConsumed: editUploadLine.unitsConsumed,
        startDate: editUploadLine.startDate,
        endDate: editUploadLine.endDate,
        ruleId: editUploadLine.ruleId,
        subsidiaryId: editUploadLine.subsidiaryId,
        countryId: editUploadLine.countryId,
        projectId: editUploadLine.projectId,
        locationId: editUploadLine.locationId,
        vehicleId: editUploadLine.vehicleId,
        contactSupplierId: editUploadLine.contactSupplierId,
        partOfEts: editUploadLine.regulatedEmissionTradingSchemeName ? true : false,
        regulatedEmissionTradingSchemeName: editUploadLine.regulatedEmissionTradingSchemeName,
        customMarketBasedMultiplier: editUploadLine.customMarketBasedMultiplier,
        isFlag: editUploadLine.isFlag === true,
        flagCategory: editUploadLine.flagCategory ?? '',
      },
      validationSchema: editUploadSchema,
      onSubmit: (values) => {
        if (values.returnId) {
          values.conversionId = values.returnId
        }
        const response = formatTransaction(values, values.groupId, true, customFields, values.categoryId)
        submitHandler(editUploadLine.id, response)
      }
    });

    useEffect(() => {
      if (editUploadLine.reportPeriodId && reportPeriodContext.allReportPeriods) {
        const reportPeriod = reportPeriodContext.allReportPeriods.find(rp => rp.id === editUploadLine.reportPeriodId)
        if (reportPeriod) {
          setAllowedDates({
            startDate: reportPeriod.startDate,
            endDate: reportPeriod.endDate,
          })
        }
      }
    }, [editUploadLine.reportPeriodId, reportPeriodContext.allReportPeriods])

    useEffect(() => {
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(() => { })

      ruleService.getAll(`type=emission-activity`)
        .then(response => {
          setRules(response)
        })
        .catch(() => { })

      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(() => { })

      categoryService.getAllCategories()
        .then(response => {
          setCategories(response)
          const category = findCategoryByText(response, editUploadLine.categoryName)
          if (category) {
            setCategoryId(category.id)
          }
        })

      locationService.getActiveLocations()
        .then(response => {
          setLocations(response)
        })
        .catch(() => { })
    }, [])

    useEffect(() => {
      const year = formik.values.startDate ? moment(formik.values.startDate).format('YYYY') : undefined
      if (year !== calendarYear) {
        setCalendarYear(year)
      }
    }, [formik.values.startDate, calendarYear])

    useEffect(() => {
      if (categoryId && group) {
        conversionSourceService.getConversionSourceForCategory(categoryId, 'conversion', group?.scope!, true)
          .then(response => {
            setConversionSources(response)
            const conversionSourceIdToMatch = (editUploadLine as any).conversionSourceId || originalFilterValues?.conversionSourceId
            if (conversionSourceIdToMatch) {
              const matched = response.find(source => source.id === conversionSourceIdToMatch)
              if (matched) {
                setConversionSourceId(matched.id)
                return
              }
            }
            if (response.length === 1) {
              setConversionSourceId(response[0].id)
              return
            }
          })
          .catch(() => { })
      }
    }, [categoryId, group, originalFilterValues])

    useEffect(() => {
      groupService.getGroupsWithCategories(false)
        .then(response => {
          setGroups(response)
          const groupId = editUploadLine.groupId
          if (groupId) {
            const foundGroup = response.find(group => group.id === groupId)
            if (foundGroup) {
              setGroup(foundGroup)
              formik.setFieldValue('groupId', foundGroup.id, false)
              setAllowedCategories([...foundGroup.categories])
              const preselectedCategory = findCategoryByText(foundGroup.categories, editUploadLine.categoryName)
              if (preselectedCategory) {
                setCategoryId(preselectedCategory.id)
                formik.setFieldValue('categoryId', preselectedCategory.id, false)
              } else {
                formik.setFieldValue('categoryName', '', false)
                setCategoryId('')
                formik.setFieldValue('categoryId', '', false)
              }
            }
          }
        })
        .catch(() => { })
    }, [])

    useEffect(() => {
      if (!displayFilters) {
        formik.setFieldValue('measurementUnitName', '');
        formik.setFieldValue('returnId', '');
      }
      if (categoryId) {
        formik.setFieldValue('categoryId', categoryId, false);
      }
    }, [categoryId]);

    useEffect(() => {
      if (locations && editUploadLine.locationId && (editUploadLine.customMarketBasedMultiplier === null || editUploadLine.customMarketBasedMultiplier === undefined)) {
        const location = locations.find(loc => loc.id === editUploadLine.locationId);
        if (location && location.locationMarketBasedConversions?.[0]?.customMarketBasedMultiplier) {
          formik.setFieldValue('customMarketBasedMultiplier', location.locationMarketBasedConversions[0].customMarketBasedMultiplier);
        }
      }
    }, [locations, editUploadLine.locationId, editUploadLine.customMarketBasedMultiplier, formik]);

    useEffect(() => {
      const recordMethod = editUploadLine.ruleId ? 'Custom Rule' : (editUploadLine as any).conversionId ? 'Filter Data' : editUploadLine.groupId ? 'Filter Data' : 'Custom Rule'
      const customFieldValuesObj: { [key: string]: string } = {}
      if (customFields && customFields.length && editUploadLine?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = editUploadLine.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? ''
          customFieldValuesObj[field.id] = matchingValue
        })
      }
      let customMarketBasedMultiplierValue = editUploadLine.customMarketBasedMultiplier;
      if ((customMarketBasedMultiplierValue === null || customMarketBasedMultiplierValue === undefined) && locations && editUploadLine.locationId) {
        const location = locations.find(loc => loc.id === editUploadLine.locationId);
        if (location && location.locationMarketBasedConversions?.[0]?.customMarketBasedMultiplier) {
          customMarketBasedMultiplierValue = location.locationMarketBasedConversions[0].customMarketBasedMultiplier;
        }
      }
      formik.setValues({
        recordMethod,
        groupId: editUploadLine.groupId,
        categoryName: editUploadLine.categoryName,
        categoryId: '',
        returnId: (editUploadLine as any).conversionId || '',
        detailLevelOne: editUploadLine.detailLevelOne,
        detailLevelTwo: editUploadLine.detailLevelTwo,
        detailLevelThree: editUploadLine.detailLevelThree,
        detailLevelFour: editUploadLine.detailLevelFour,
        detailLevelFive: editUploadLine.detailLevelFive,
        measurementUnitName: editUploadLine.measurementUnitName,
        unitsConsumed: editUploadLine.unitsConsumed,
        startDate: editUploadLine.startDate,
        endDate: editUploadLine.endDate,
        contactSupplierId: editUploadLine.contactSupplierId,
        ruleId: editUploadLine.ruleId,
        subsidiaryId: editUploadLine.subsidiaryId,
        countryId: editUploadLine.countryId,
        projectId: editUploadLine.projectId,
        locationId: editUploadLine.locationId,
        vehicleId: editUploadLine.vehicleId,
        partOfEts: editUploadLine.regulatedEmissionTradingSchemeName ? true : false,
        regulatedEmissionTradingSchemeName: editUploadLine.regulatedEmissionTradingSchemeName,
        customMarketBasedMultiplier: customMarketBasedMultiplierValue,
        isFlag: editUploadLine.isFlag === true,
        flagCategory: editUploadLine.flagCategory ?? '',
        ...customFieldValuesObj,
      }, false);
      setOriginalRuleId(editUploadLine.ruleId || null)
      setOriginalConversionId((editUploadLine as any).conversionId || null)
      if ((editUploadLine as any).conversionId) {
        setOriginalFilterValues({
          groupId: editUploadLine.groupId,
          categoryName: editUploadLine.categoryName,
          detailLevelOne: editUploadLine.detailLevelOne,
          detailLevelTwo: editUploadLine.detailLevelTwo,
          detailLevelThree: editUploadLine.detailLevelThree,
          detailLevelFour: editUploadLine.detailLevelFour,
          detailLevelFive: editUploadLine.detailLevelFive,
          measurementUnitName: editUploadLine.measurementUnitName,
          conversionSourceId: (editUploadLine as any).conversionSourceId,
        })
      } else {
        setOriginalFilterValues(null)
      }
      if (groups && editUploadLine.groupId) {
        const foundGroup = groups.find(g => g.id === editUploadLine.groupId)
        if (foundGroup) {
          setGroup(foundGroup)
          formik.setFieldValue('groupId', foundGroup.id, false)
          setAllowedCategories([...foundGroup.categories])
          if (editUploadLine.categoryName) {
            const foundCategory = findCategoryByText(foundGroup.categories, editUploadLine.categoryName)
            if (foundCategory) {
              setCategoryId(foundCategory.id)
              formik.setFieldValue('categoryId', foundCategory.id, false)
            }
          }
        }
      }
    }, [editUploadLine, groups, customFields, locations]);

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setFieldValue(name, date.format('YYYY-MM-DD'));
      }
    }
    const recordMethodHandler = (e: SelectChangeEvent<string>) => {
      formik.setFieldValue('recordMethod', e.target.value)
      if (e.target.value === 'Custom Rule') {
        if (originalRuleId) {
          formik.setFieldValue('ruleId', originalRuleId)
        } else {
          formik.setFieldValue('ruleId', '')
        }
        formik.setFieldValue('returnId', '')
        setConversionSourceId(undefined)
        formik.setFieldValue('groupId', '')
        formik.setFieldValue('categoryName', '')
        formik.setFieldValue('categoryId', '')
        formik.setFieldValue('measurementUnitName', '')
        formik.setFieldValue('detailLevelOne', '')
        formik.setFieldValue('detailLevelTwo', '')
        formik.setFieldValue('detailLevelThree', '')
        formik.setFieldValue('detailLevelFour', '')
        formik.setFieldValue('detailLevelFive', '')
        setGroup(undefined)
        setCategoryId('')
        setAllowedCategories([])
      } else if (e.target.value === 'Filter Data') {
        formik.setFieldValue('ruleId', '')
        if (originalFilterValues) {
          formik.setFieldValue('groupId', originalFilterValues.groupId || '')
          formik.setFieldValue('categoryName', originalFilterValues.categoryName || '')
          formik.setFieldValue('detailLevelOne', originalFilterValues.detailLevelOne || '')
          formik.setFieldValue('detailLevelTwo', originalFilterValues.detailLevelTwo || '')
          formik.setFieldValue('detailLevelThree', originalFilterValues.detailLevelThree || '')
          formik.setFieldValue('detailLevelFour', originalFilterValues.detailLevelFour || '')
          formik.setFieldValue('detailLevelFive', originalFilterValues.detailLevelFive || '')
          formik.setFieldValue('measurementUnitName', originalFilterValues.measurementUnitName || '')
          formik.setFieldValue('returnId', originalConversionId || '')
          if (originalFilterValues.groupId && groups) {
            const foundGroup = groups.find(g => g.id === originalFilterValues.groupId)
            if (foundGroup) {
              setGroup(foundGroup)
              formik.setFieldValue('groupId', foundGroup.id, false)
              setAllowedCategories([...foundGroup.categories])
              if (originalFilterValues.categoryName) {
                const foundCategory = findCategoryByText(foundGroup.categories, originalFilterValues.categoryName)
                if (foundCategory) {
                  setCategoryId(foundCategory.id)
                  formik.setFieldValue('categoryId', foundCategory.id, false)
                }
              }
            }
          }
        }
      }
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries?.find(s => s.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
      }
    }

    const selectGroupHandler = (value: string) => {
      const oldGroupId = formik.values.groupId
      const newGroup = groups?.find(group => group.id === value)
      if (oldGroupId !== value) {
        formik.setFieldValue('groupId', value);
        setGroup(newGroup)
        setAllowedCategories(newGroup?.categories)
        formik.setFieldValue('categoryName', '')
        formik.setFieldValue('categoryId', '', false)
        setCategoryId('')
        formik.setFieldValue('detailLevelOne', '')
        formik.setFieldValue('detailLevelTwo', '')
        formik.setFieldValue('detailLevelThree', '')
        formik.setFieldValue('detailLevelFour', '')
        formik.setFieldValue('detailLevelFive', '')
        formik.setFieldValue('measurementUnitName', '')
        setConversionSourceId(undefined)
        setConversionSources(undefined)
      }
    }

    const selectCategoryHandler = (value: string) => {
      setDisplayFilters(false)
      const category = findCategoryByText(categories, value)
      if (category) {
        const oldCategoryName = formik.values.categoryName
        formik.setFieldValue('categoryName', value);
        formik.setFieldValue('categoryId', category.id, false);
        setCategoryId(category.id)
        if (oldCategoryName !== value) {
          formik.setFieldValue('detailLevelOne', '')
          formik.setFieldValue('detailLevelTwo', '')
          formik.setFieldValue('detailLevelThree', '')
          formik.setFieldValue('detailLevelFour', '')
          formik.setFieldValue('detailLevelFive', '')
          formik.setFieldValue('measurementUnitName', '')
        }
        conversionSourceService.getConversionSourceForCategory(category.id, 'conversion', group?.scope!, true)
          .then(response => {
            setConversionSources(response)
          })
          .catch(() => { })
      }
    }

    const conversionSourceHandler = (e: SelectChangeEvent<string>) => {
      const conversionSource = conversionSources?.find(source => source.id === e.target.value)
      if (conversionSource) {
        setConversionSourceId(conversionSource.id)
      }
    }

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    // Save must not silently no-op. formik.submitForm() aborts without calling
    // onSubmit when validation fails, and some required fields here have no
    // visible control (e.g. Country, which is derived from the subsidiary), so
    // an incomplete "missing data" row would otherwise appear to just not save.
    // Validate first, mark errored fields touched, and surface what's missing.
    const handleSave = async () => {
      const errors = await formik.validateForm()
      const errorKeys = Object.keys(errors)
      if (errorKeys.length > 0) {
        formik.setTouched(errorKeys.reduce<Record<string, boolean>>((touched, key) => ({ ...touched, [key]: true }), {}))
        const messages = Array.from(new Set(Object.values(errors).filter(Boolean) as string[]))
        userNotificationsContext.openSnackbar({
          open: true,
          message: `Cannot save. Please complete: ${messages.join(', ')}`,
          backgroundColor: 'text.primary',
        })
        return
      }
      formik.submitForm()
    }

    useEffect(() => {
    }, [categoryId, group, editUploadLine.reportPeriodId, conversionSourceId])

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            Edit Uploaded Data
            {/* A row read off a bill: open the bill in a new tab, so its figures can be typed or
                checked with the document alongside. */}
            {!!editUploadLine.billDocumentId && (
              <Button
                variant='outlined'
                size='small'
                startIcon={<OpenInNewOutlined fontSize='small' />}
                onClick={viewBillHandler}
                sx={{ flexShrink: 0 }}
              >
                <Typography variant='body2' sx={{ pl: 0.5 }}>View bill</Typography>
              </Button>
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label='Start Date'
                  value={formik.values.startDate ? moment(formik.values.startDate) : null}
                  onChange={(date) => dateHandler(date, 'startDate')}
                  format='DD/MM/YYYY'
                  maxDate={moment(allowedDates.endDate)}
                  minDate={moment(allowedDates.startDate)}
                  sx={{ color: 'text.primary' }}
                  open={openStartDate}
                  onOpen={() => setOpenStartDate(true)}
                  onClose={() => setOpenStartDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenStartDate(true),
                      error: formik.touched.startDate && Boolean(formik.errors.startDate)
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.startDate && formik.errors.startDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  disabled={formik.values.startDate ? false : true}
                  label='End Date'
                  maxDate={moment(allowedDates.endDate)}
                  minDate={moment(formik.values.startDate) || moment(allowedDates.startDate)}
                  value={formik.values.endDate ? moment(formik.values.endDate) : null}
                  onChange={(date) => dateHandler(date, 'endDate')}
                  format='DD/MM/YYYY'
                  sx={{ color: 'text.primary' }}
                  open={openEndDate}
                  onOpen={() => setOpenEndDate(true)}
                  onClose={() => setOpenEndDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenEndDate(true),
                      error: formik.touched.endDate && Boolean(formik.errors.endDate),
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.endDate && formik.errors.endDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.endDate ? <div>{formik.errors.endDate as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
            <FormControl sx={{ mt: 2, ml: 1 }}>
              <RadioGroup
                onChange={recordMethodHandler}
                value={formik.values.recordMethod}
                id={'recordMethod'} name={'recordMethod'}>
                <Stack display='flex' flexDirection='row' alignItems='center'>
                  <FormControlLabel value={'Custom Rule'} control={<Radio />} label='Rule' />
                  <FormControlLabel value={'Filter Data'} control={<Radio />} label='Manual'
                  />
                </Stack>
              </RadioGroup>
            </FormControl>
            {rules && formik.values.recordMethod === 'Custom Rule' && <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel htmlFor='ruleId'>Custom Rule</InputLabel>
              <Select
                value={formik.values.ruleId || ''}
                onChange={(e) => {
                  const ruleId = e.target.value as string
                  formik.setFieldValue('ruleId', ruleId)
                }}
                fullWidth
                label="Custom Rule"
                error={formik.touched.ruleId && Boolean(formik.errors.ruleId)}
                name="ruleId"
                id='ruleId'>
                {rules.sort((a, b) => a.value.localeCompare(b.value)).map((rule, index) => (
                  <MenuItem key={index}
                    value={rule.id}>{rule.value}</MenuItem>
                ))}
              </Select>
              {formik.touched.ruleId && formik.errors.ruleId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.ruleId ? <div>{formik.errors.ruleId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>}
            {formik.values.recordMethod === 'Filter Data' &&
              <>
                {groups && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='groupId'>Group</InputLabel>
                  <Select
                    id='groupId' label='Group' name='groupId'
                    value={formik.values.groupId || ''}
                    onChange={(e) => selectGroupHandler(e.target.value)}
                    disabled={!formik.values.startDate}
                    error={formik.touched.groupId && Boolean(formik.errors.groupId)}
                    sx={{ color: 'text.primary', }}>
                    {sortGroupsByGhgCategory(groups).map((group, index) =>
                      <MenuItem key={index} value={group.id}>{formatGroupLabel(group)}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.groupId && formik.errors.groupId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.groupId ? <div>{formik.errors.groupId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>}
                {categories && allowedCategories && allowedCategories.length > 0 && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='categoryName'>Category</InputLabel>
                  <Select
                    key={`category-${allowedCategories.length}-${formik.values.categoryName}`}
                    id='categoryName' label='Category' name='categoryName'
                    value={formik.values.categoryName}
                    onChange={(e) => selectCategoryHandler(e.target.value)}
                    disabled={!formik.values.startDate}
                    error={formik.touched.categoryName && Boolean(formik.errors.categoryName)}
                    sx={{ color: 'text.primary', }}>
                    {allowedCategories.map((category, index) =>
                      <MenuItem key={index} value={category.name}>{getCategoryDisplayName(category)}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.categoryName && formik.errors.categoryName &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.categoryName ? <div>{formik.errors.categoryName as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>}
                {conversionSources && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='conversionSourceId'>Conversion Source</InputLabel>
                  <Select
                    id='conversionSourceId' label='Conversion Source' name='conversionSourceId'
                    value={conversionSourceId || ''}
                    onChange={conversionSourceHandler}
                    disabled={!formik.values.startDate}
                    sx={{ color: 'text.primary', }}>
                    {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((source, index) => (
                      <MenuItem key={index} value={source.id}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <span>{source.source}</span>
                          {source.country && (
                            <img
                              loading="lazy"
                              width="20"
                              src={source.country.imageUrl}
                              alt={`${source.country.name} flag`}
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>}
                {!conversionSources && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='conversionSourceId'>Conversion Source</InputLabel>
                  <Select
                    id='conversionSourceId' label='Conversion Source' name='conversionSourceId'
                    disabled>
                    <MenuItem> Conversion Source</MenuItem>
                  </Select>
                </FormControl>}
                {categoryId && group && calendarYear && conversionSourceId &&
                  <Stack sx={{ mt: 2 }}>
                    <EmissionsFilterLogic
                      categoryId={categoryId}
                      groupId={group.id}
                      calendarYear={calendarYear}
                      defaultConversionSourceId={conversionSourceId}
                      inputValues={{
                        detailLevelOne: formik.values.detailLevelOne,
                        detailLevelTwo: formik.values.detailLevelTwo,
                        detailLevelThree: formik.values.detailLevelThree,
                        detailLevelFour: formik.values.detailLevelFour,
                        detailLevelFive: formik.values.detailLevelFive,
                        measurementUnitName: formik.values.measurementUnitName,
                      }}
                      formik={formik}
                      isEdit={true}
                      direction='column' />
                  </Stack>}
                {(!conversionSourceId || !group || !categoryId || !editUploadLine.reportPeriodId) &&
                  <Stack sx={{ mt: 2 }}>
                    <FormControl fullWidth variant='outlined'>
                      <InputLabel htmlFor='additionalInformation'>Factor Detail 1</InputLabel>
                      <Select
                        disabled={true}>
                      </Select>
                    </FormControl>
                  </Stack>}
              </>}
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='unitsConsumed'>Units Consumed</InputLabel>
              <OutlinedInput inputProps={{ min: 0 }} id='unitsConsumed' label='Units Consumed' name='unitsConsumed' type='number'
                value={formik.values.unitsConsumed}
                onChange={formik.handleChange}
                error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.unitsConsumed ? <div>{formik.errors.unitsConsumed as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
            {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
                <Select
                  id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} name='subsidiaryId'
                  value={formik.values.subsidiaryId || ''}
                  onChange={subsidiaryHandler}
                  error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                  sx={{ color: 'text.primary', }}>
                  {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                    <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                  )}
                </Select>
                {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>}
            {customFields &&
              <EmissionsInputAdvanced
                isEdit={true}
                customFields={customFields}
                formik={formik}
                hideRegulatedEmissionTradingScheme={isEtsGroupId(formik.values.groupId)}
                hideRec={true}
                hideExcludedFromReporting={true}
                hideEnergySupplierTariff={true}
                hideCustomBasedMarketMultiplier={!(formik.values.recordMethod === 'Filter Data' && categoryId === ELECTRICITY_CATEGORY_ID)}
                showOnlyCustomBasedMarketMultiplier={true}
              />}
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={handleSave}
              disableSaveButton={(formik.values.recordMethod === 'Filter Data' && !formik.values.measurementUnitName) || (formik.values.recordMethod === 'Custom Rule' && !formik.values.ruleId)}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }